import {IItemSearchResponse, ProductList} from "../typescript-library";

export type StoreDataType = {
  current?: IItemSearchResponse;
  preload?: IItemSearchResponse;
};

export type StoreEntryType = StoreDataType & {
  query: string;
  limit: number;
  timestamp: number;
};

interface IDBCursorWithTypedValue extends IDBCursor {
  value: StoreEntryType | undefined;
}

/*
-------- Linter is complaining--------
// In the following line, you should include the prefixes of implementations you want to test.
window.indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
// DON'T use "var indexedDB = ..." if you're not in a function.
// Moreover, you may need references to some window.IDB* objects:
window.IDBTransaction = window.IDBTransaction || window.webkitIDBTransaction || window.msIDBTransaction;
window.IDBKeyRange = window.IDBKeyRange || window.webkitIDBKeyRange || window.msIDBKeyRange;
// (Mozilla has never prefixed these objects, so we don't need window.mozIDB*)
*/

const dbName = "ProductListDB";
const storeName = "product_lists";
const timeout = 3600000; // 1000ms * 60s * 60min = 1 hour

const openDatabase = () => {
  const DBOpenRequest = window.indexedDB.open(dbName, 1);

  return new Promise<IDBDatabase>((resolve, reject) => {
    DBOpenRequest.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(storeName)) {
        const store = db.createObjectStore(storeName, {keyPath: "query"});
        // Just for overview purposes in the browser dev tools
        store.createIndex("limit", "limit", {unique: false});
      }
    };

    DBOpenRequest.onsuccess = ({target}) => resolve((target as IDBRequest<IDBDatabase>).result);
    DBOpenRequest.onerror = ({target}) => reject((target as IDBRequest<IDBDatabase>).result);
  });
};

const setValue = async (database: IDBDatabase, value: StoreEntryType, limit: number) => {
  return new Promise((resolve, reject) => {
    const setData = () => {
      const obStore = database.transaction([storeName], "readwrite").objectStore(storeName);

      const request = obStore.put(value);
      request.onsuccess = ({target}) => {
        resolve((target as IDBRequest<string>).result);
        database.close();
      };
      request.onerror = ({target}) => {
        reject((target as IDBRequest).error);
        database.close();
      };
    };

    if (value.current) {
      // Page load/offset === 0, no value shouldn't have been found upfront under the same key.
      // See `ProductListProvider.fetchItems()`
      setData();
    } else {
      // "View more" Button: `updateValue` should only contain the newly fetched `preload`
      void getData(database, {query: value.query, limit, updateValue: value}).then(
        (alreadyExisting: StoreEntryType | null) => {
          if (!alreadyExisting) {
            // Should never be the case
            setData();
          } else {
            database.close();
          }
        },
      );
    }
  });
};

/**
 * Gets a value out of the database, and, depending on params, does some updates
 * @param database
 * @param params
 */
const getData = (
  database: IDBDatabase,
  params: {query: string; limit: number; updateValue?: StoreEntryType; swap?: true},
) =>
  new Promise<StoreEntryType | null>((resolve, reject) => {
    const objectStore = database.transaction([storeName], "readwrite").objectStore(storeName);

    const request = objectStore.openCursor(null, "prev");
    request.onsuccess = ({target}) => {
      const cursor = (target as IDBRequest<IDBCursorWithTypedValue>).result;
      if (!cursor) {
        // End of the list of entries, if not formerly resolved, do so with null
        resolve(null);
      } else if (!cursor.value) {
        // No value, e.g. an entry that was marked as deleted in a previous iteration
        cursor.continue();
      } else if (Date.now() - cursor.value.timestamp > timeout) {
        // Expired entry cleanup (affects _all_ entries)
        cursor.delete();
        cursor.continue();
      } else {
        // Entry that matches the request param string
        if (params.query === cursor.key) {
          const value = cursor.value;
          // Entry that exactly matches the limit param, like e.g. updating on new preload, or returning back to the page
          if (params.limit === value.limit) {
            // On "swap" param, push preload to the end of current, and clear preload
            if (params.swap && value.preload) {
              // Type safety, current should've been set by `setValue()` (double load on offset 0)
              if (value.current) {
                value.current.itemContent.push(...value.preload.itemContent);
              } else {
                value.current = value.preload;
              }
              value.preload = undefined;
              value.limit = params.limit + ProductList.PRODUCT_LIST_DEFAULT_LIMIT;
              cursor.update(cursor.value);
            } else if (params.updateValue) {
              // older values get the new timestamp and can last longer.
              // when newer values should get the soon-to-be-outdated timestamp,
              // just delete, or comment out that next line
              value.timestamp = params.updateValue.timestamp;

              if (params.updateValue.preload) {
                // Seems new preload content. Replace.
                value.preload = params.updateValue.preload;
              }

              cursor.update(cursor.value);
            }
            resolve(cursor.value);

            return;
          }
          if (params.limit < value.limit && value.current && value.preload) {
            // get the least amount of items out of an older entry
            let tempItemContent = [...value.current.itemContent, ...value.preload.itemContent];
            tempItemContent = tempItemContent.slice(0, params.limit + ProductList.PRODUCT_LIST_DEFAULT_LIMIT);
            value.preload.itemContent = tempItemContent.splice(params.limit);
            value.current.itemContent = tempItemContent;
            cursor.update(cursor.value);
            resolve(cursor.value);

            return;
          }
        }
        cursor.continue();
      }
    };
    request.onerror = ({target}) => {
      reject((target as IDBRequest).error);
      database.close();
    };
  });

/**
 * Get the necessary data out of the database, and closes the database
 * @param database
 * @param query
 * @param limit
 */
const getValue = async (database: IDBDatabase, query: string, limit: number): Promise<StoreEntryType | null> => {
  return await getData(database, {query, limit}).then((data: StoreEntryType | null) => {
    database.close();

    return data;
  });
};

/**
 * Swaps `preload` content to the end of the `current` content
 * @param database
 * @param query
 * @param limit
 */
const swapData = async (database: IDBDatabase, query: string, limit: number) =>
  await getData(database, {query, limit, swap: true});

/**
 * Pretty much a copy of <https://github.com/julienetie/db64>
 */
const db64 = {
  set: async (value: StoreEntryType, offset: number) =>
    openDatabase().then((database) => setValue(database, value, offset)),
  get: async (key: string, limit: number) => openDatabase().then((database) => getValue(database, key, limit)),
  swap: async (key: string, offset: number) => openDatabase().then((database) => swapData(database, key, offset)),
};

export default db64;
